import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem, Product, SelectedProductOption } from '../types';

interface CartStore {
    items: CartItem[];
    isOpen: boolean;
    addItem: (product: Product, quantity?: number, selectedOptions?: SelectedProductOption[], unitPrice?: number) => void;
    removeItem: (itemId: string) => void;
    updateQuantity: (itemId: string, quantity: number) => void;
    clearCart: () => void;
    toggleCart: () => void;
    total: () => number;
    itemCount: () => number;
}

const buildCartItemId = (productId: string, selectedOptions: SelectedProductOption[]) => {
    const optionsKey = [...selectedOptions]
        .sort((left, right) => `${left.groupId}:${left.itemId}`.localeCompare(`${right.groupId}:${right.itemId}`))
        .map((option) => `${option.groupId}:${option.itemId}`)
        .join('|');

    return optionsKey ? `${productId}__${optionsKey}` : productId;
};

const calculateUnitPrice = (product: Product, selectedOptions: SelectedProductOption[]) =>
    (() => {
        const absoluteOptions = selectedOptions.filter((option) => option.pricingMode === 'ABSOLUTE');
        const extraOptions = selectedOptions.filter((option) => option.pricingMode === 'EXTRA');
        const basePrice = absoluteOptions.length > 0
            ? absoluteOptions.reduce((sum, option) => sum + option.price, 0)
            : product.price;

        return basePrice + extraOptions.reduce((sum, option) => sum + (option.effectivePrice ?? option.price), 0);
    })();

export const useCartStore = create<CartStore>()(
    persist(
        (set, get) => ({
            items: [],
            isOpen: false,

            addItem: (product, quantity = 1, selectedOptions = [], providedUnitPrice) => {
                set((state) => {
                    const itemId = buildCartItemId(product.id, selectedOptions);
                    const unitPrice = typeof providedUnitPrice === 'number'
                        ? providedUnitPrice
                        : calculateUnitPrice(product, selectedOptions);
                    const existing = state.items.find((item) => item.id === itemId);

                    if (existing) {
                        return {
                            items: state.items.map((item) =>
                                item.id === itemId
                                    ? { ...item, quantity: item.quantity + quantity, subtotal: (item.quantity + quantity) * item.unitPrice }
                                    : item
                            ),
                        };
                    }

                    return {
                        items: [
                            ...state.items,
                            {
                                id: itemId,
                                product,
                                quantity,
                                unitPrice,
                                selectedOptions,
                                subtotal: unitPrice * quantity,
                            },
                        ],
                    };
                });
            },

            removeItem: (itemId) => {
                set((state) => ({ items: state.items.filter((item) => item.id !== itemId) }));
            },

            updateQuantity: (itemId, quantity) => {
                if (quantity <= 0) {
                    get().removeItem(itemId);
                    return;
                }
                set((state) => ({
                    items: state.items.map((item) =>
                        item.id === itemId
                            ? { ...item, quantity, subtotal: quantity * item.unitPrice }
                            : item
                    ),
                }));
            },

            clearCart: () => set({ items: [] }),
            toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),
            total: () => get().items.reduce((acc, item) => acc + item.subtotal, 0),
            itemCount: () => get().items.reduce((acc, item) => acc + item.quantity, 0),
        }),
        {
            name: 'gastroflow-cart',
            version: 2,
            migrate: (persistedState: any) => {
                if (!persistedState?.items) {
                    return persistedState;
                }

                return {
                    ...persistedState,
                    items: persistedState.items.map((item: any) => {
                        const selectedOptions = Array.isArray(item.selectedOptions) ? item.selectedOptions : [];
                        const unitPrice = typeof item.unitPrice === 'number'
                            ? item.unitPrice
                            : (item.product?.price || 0) + selectedOptions.reduce((sum: number, option: any) => sum + (option.effectivePrice ?? option.price ?? 0), 0);

                        return {
                            ...item,
                            id: item.id || buildCartItemId(item.product.id, selectedOptions),
                            unitPrice,
                            selectedOptions,
                            subtotal: typeof item.subtotal === 'number' ? item.subtotal : unitPrice * (item.quantity || 1),
                        };
                    }),
                };
            },
        }
    )
);
